import { isAfter, isFuture, isToday, isYesterday, parseISO, startOfDay } from 'date-fns'
import { DateTime } from 'luxon'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import {
  SweepstakeObject,
  TheatricalDiscount,
  TheatricalReleaseObject,
  TicketsStatusReference,
} from '@/types/codegen-federation'
import { SimpleTicketItem } from '@/views/TicketCheckoutViews/hooks/useSimpleTicketList'
import { CurrentTheatricalRelease, PastTheatricalRelease } from './types'

export const mapToCurrentTheatricalRelease = (release: TheatricalReleaseObject): Partial<CurrentTheatricalRelease> => {
  return {
    theatricalSlug: release.theatricalSlug,
    ticketPageLink: paths.tickets.checkout.showtimes(release.theatricalSlug),
    hubPageLink: `/movies/${release.theatricalSlug}`,
    backgroundVideo:
      release.theatricalSlug === slugs.sketch
        ? 'https://content.angelstudios.com/sketch/SKETCH%20Trailer%20Loop.mp4'
        : release.theatricalSlug === slugs.theLastRodeo
        ? 'https://content.angelstudios.com/the-last-rodeo/last-rodeo-hero-vid-loop-02a.mp4'
        : null,
    nonTitleArt16x9Image: release?.images?.nonTitleArt?.image16x9 ?? undefined,
    nonTitleArt1x1Image: release?.images?.nonTitleArt?.image1x1 ?? undefined,
    nonTitleArt2x3Image: release?.images?.nonTitleArt?.image2x3 ?? undefined,
    angelKeyArt16x9Image: release?.images?.angelKeyArt1?.image16x9 ?? undefined,
    angelKeyArt2x3Image: release?.images?.angelKeyArt1?.image2x3 ?? undefined,
    logo: release?.images?.logo?.image2x1 ?? undefined,
    title: release?.title ?? undefined,
    releaseDate: release?.releaseDate ?? undefined,
    ticketStatus: release?.region?.ticketsStatus ?? undefined,
    duration: release?.movieDuration ?? undefined,
    rating: release?.movieRating ?? undefined,
    isFeatureRelease: !!release?.isFeaturedRelease,
    trailerUrl: release?.trailerUrl ?? release?.region?.trailerUrl ?? undefined,
    tomatoesScore: getTomatoesScore(release?.critics),
    audienceScore: getAudienceScore(release?.critics),
  }
}

const getTomatoesScore = (critics: TheatricalReleaseObject['critics']) => {
  return critics
    ?.find((critic) => critic?.company === 'Rotten Tomatoes')
    ?.scores?.find((score) => score?.name === 'Tomatometer')?.value
}
const getAudienceScore = (critics: TheatricalReleaseObject['critics']) => {
  return critics
    ?.find((critic) => critic?.company === 'Rotten Tomatoes')
    ?.scores?.find((score) => score?.name === 'Audience')?.value
}

export const isValidCurrentTheatricalRelease = (
  release: Partial<CurrentTheatricalRelease>,
): release is CurrentTheatricalRelease => {
  if (!release) return false

  return (
    typeof release.theatricalSlug === 'string' &&
    typeof release.nonTitleArt16x9Image === 'string' &&
    typeof release.nonTitleArt1x1Image === 'string' &&
    typeof release.angelKeyArt16x9Image === 'string' &&
    typeof release.angelKeyArt2x3Image === 'string' &&
    typeof release.logo === 'string' &&
    typeof release.title === 'string' &&
    typeof release.releaseDate === 'string' &&
    typeof release.isFeatureRelease === 'boolean' &&
    typeof release.ticketStatus === 'string'
  )
}

export const mapToPastTheatricalRelease = (release: TheatricalReleaseObject) => {
  return {
    theatricalSlug: release.theatricalSlug,
    image: release?.images?.angelKeyArt1?.image2x3 ?? undefined,
    link: `/watch/${release.theatricalSlug}`,
    releaseDate: release?.releaseDate ?? undefined,
  }
}

export const isValidPastTheatricalRelease = (
  release: Partial<PastTheatricalRelease>,
): release is PastTheatricalRelease => {
  if (!release) return false

  return (
    typeof release.theatricalSlug === 'string' &&
    typeof release.image === 'string' &&
    typeof release.releaseDate === 'string'
  )
}

export const byTheatricalReleaseDateAsc = (
  a: CurrentTheatricalRelease | PastTheatricalRelease,
  b: CurrentTheatricalRelease | PastTheatricalRelease,
) => new Date(a.releaseDate).getTime() - new Date(b.releaseDate).getTime()

export const byTheatricalReleaseDateDesc = (
  a: CurrentTheatricalRelease | PastTheatricalRelease,
  b: CurrentTheatricalRelease | PastTheatricalRelease,
) => new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime()

export const bySweepstakeEndDateAsc = (a: SweepstakeObject, b: SweepstakeObject) =>
  new Date(a.endDate).getTime() - new Date(b.endDate).getTime()

export const isTodayOrInFuture = (utcDate: string) => {
  const now = DateTime.now().startOf('day')
  const showtimeDate = DateTime.fromISO(utcDate).startOf('day')
  return showtimeDate >= now
}

export const isInFutureOrWithin3HoursAfter = (utcDate: string) => {
  const now = DateTime.now()
  const showtimeDatePlus3Hours = DateTime.fromISO(utcDate).plus({ hours: 3 })

  return now <= showtimeDatePlus3Hours
}

export function isInTheaters({ ticketStatus, releaseDate }: CurrentTheatricalRelease) {
  if (ticketStatus === 'LIVE') return true

  return isYesterday(startOfDay(releaseDate)) || isToday(startOfDay(releaseDate) || isFuture(startOfDay(releaseDate)))
}

export function canUseGuildCodes(status: TicketsStatusReference) {
  return status === 'PRE_SALES' || status === 'LIVE'
}

export const getTodaysUserTicketsBySlug = (userTickets: SimpleTicketItem[], theatricalSlug: string) => {
  return userTickets.filter((ticket) => {
    if (!ticket?.showtimeSummary?.utcStartTime) return false
    if (ticket.theatricalSlug !== theatricalSlug) return false

    const utcDate = parseISO(ticket.showtimeSummary.utcStartTime) // assumes string is ISO format
    const now = new Date()

    return isToday(utcDate) && isAfter(utcDate, now)
  })
}

export const extractCurrentReleases = ({
  releases,
  guildDiscounts,
  sweepstakes,
}: {
  releases: TheatricalReleaseObject[]
  guildDiscounts?: TheatricalDiscount[]
  sweepstakes?: SweepstakeObject[]
}): CurrentTheatricalRelease[] => {
  return releases
    .filter((release) => release?.region?.ticketsStatus !== 'ENDED')
    .map(mapToCurrentTheatricalRelease)
    .filter(isValidCurrentTheatricalRelease)
    .map((release) => addGuildCodesToRelease(release, guildDiscounts))
    .map((release) => addSweepstakeBooleanToRelease(release, sweepstakes))
    .map((release) => ({
      ...release,
      duration: release.duration ?? null,
    }))
    .sort(byTheatricalReleaseDateAsc)
}

const addSweepstakeBooleanToRelease = (
  release: CurrentTheatricalRelease,
  sweepstakes: SweepstakeObject[] | undefined,
): CurrentTheatricalRelease => {
  const activeSweepstake = !!sweepstakes?.some((sweepstake) => sweepstake?.slug === release.theatricalSlug)

  return { ...release, activeSweepstake }
}

const addGuildCodesToRelease = (
  release: CurrentTheatricalRelease,
  guildDiscounts: TheatricalDiscount[] | undefined,
): CurrentTheatricalRelease => {
  const guildCodes = guildDiscounts
    ?.filter((discount) => discount?.theatricalSlug === release.theatricalSlug)
    ?.map((discount) => discount?.code)

  return { ...release, guildCodes }
}
